import {
  createAction,
  createAsyncThunk,
  createSelector,
  createSlice,
  PayloadAction,
  appLogout,
  injectSlice,
  persistReducer,
} from '@betty/toolkit/state';
import { AsyncStorage } from '@betty/toolkit/storage';
import StatusService from '../services/StatusService/StatusService';
import { StatusResponseType, StatusState } from './status.types';
import { RewardsService } from '@betty/toolkit/services';
import StatusConfigService from '../services/StatusService/StatusConfigService';

const initialState: StatusState = {
  data: undefined,
  dataState: 'idle',
  claimingRewardIds: [],
  claimAllRewardsDataState: 'idle',
  statusConfig: {},
  statusConfigDataState: 'idle',
};

export const statusUpdated = createAction<StatusResponseType>('status/updated');

export const fetchStatus = createAsyncThunk(
  'status/fetch',
  async (_, thunkApi) => {
    const response = await StatusService.getStatus();

    thunkApi.dispatch(updateStatus(response));
  },
);

export const fetchStatusConfig = createAsyncThunk(
  'status/fetchConfig',
  async () => {
    const response = await StatusConfigService.getStatusConfig();

    return response;
  },
);

export const acknowledgeStatus = createAsyncThunk(
  'status/acknowledge',
  async () => StatusService.acknowledgeStatus(),
);

export const claimStatusReward = createAsyncThunk(
  'status/claimBox',
  (rewardId: number) =>
    RewardsService.claimReward({ pathParams: { id: rewardId } }),
);

export const claimAllStatusRewards = createAsyncThunk(
  'status/claimAllBoxes',
  async () => StatusService.claimAllStatusRewards(),
);

const status = createSlice({
  name: 'status',
  initialState,
  reducers: {
    updateStatus(state, action: PayloadAction<StatusResponseType>) {
      state.data = {
        ...state.data,
        ...action.payload,
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchStatus.pending, (state) => {
      state.dataState =
        state.dataState === 'fulfilled' ? 'refreshing' : 'pending';
    });

    builder.addCase(fetchStatus.rejected, (state) => {
      state.dataState = 'rejected';
    });

    builder.addCase(fetchStatus.fulfilled, (state) => {
      state.dataState = 'fulfilled';
    });

    builder.addCase(fetchStatusConfig.pending, (state) => {
      state.statusConfigDataState =
        state.dataState === 'fulfilled' ? 'refreshing' : 'pending';
    });

    builder.addCase(fetchStatusConfig.rejected, (state) => {
      state.statusConfigDataState = 'rejected';
    });

    builder.addCase(fetchStatusConfig.fulfilled, (state, action) => {
      state.statusConfigDataState = 'fulfilled';
      state.statusConfig = action.payload || {};
    });

    builder.addCase(claimAllStatusRewards.pending, (state) => {
      state.claimAllRewardsDataState = 'pending';
    });

    builder.addCase(claimAllStatusRewards.fulfilled, (state) => {
      state.claimAllRewardsDataState = 'fulfilled';

      if (state.data?.pendingRewards) {
        state.data.pendingRewards = [];
      }
    });

    builder.addCase(claimAllStatusRewards.rejected, (state) => {
      state.claimAllRewardsDataState = 'rejected';
    });

    builder.addCase(claimStatusReward.pending, (state, action) => {
      state.claimingRewardIds.push(action.meta.arg);
    });

    builder.addCase(claimStatusReward.fulfilled, (state, action) => {
      if (state.data?.pendingRewards) {
        state.data.pendingRewards = state.data?.pendingRewards.filter(
          ({ rewardId }) => rewardId !== action.meta.arg,
        );
      }
    });

    builder.addCase(appLogout, (state) => {
      Object.assign(state, initialState);
    });

    builder.addMatcher(claimStatusReward.settled, (state, action) => {
      state.claimingRewardIds = state.claimingRewardIds.filter(
        (id) => id !== action.meta.arg,
      );
    });
  },
});

export const { updateStatus } = status.actions;

const persistConfig = {
  key: 'status',
  storage: AsyncStorage,
  whitelist: ['data', 'points', 'currentLevel'],
};

const persistedReducer = persistReducer(persistConfig, status.reducer);

export const withStatus = injectSlice({
  reducerPath: 'status',
  reducer: persistedReducer,
});

export const selectStatus = withStatus.selector((state) => state.status.data);

export const selectStatusDataState = withStatus.selector(
  (state) => state.status.dataState,
);

export const selectStatusConfig = withStatus.selector(
  (state) => state.status.statusConfig,
);

export const selectStatusConfigDataState = withStatus.selector(
  (state) => state.status.statusConfigDataState,
);

export const selectClaimingRewardIds = withStatus.selector(
  (state) => state.status.claimingRewardIds,
);

export const selectIsClaimingReward = createSelector(
  [selectClaimingRewardIds, (_, rewardId) => rewardId],
  (ids, rewardId) => ids.indexOf(rewardId) !== -1,
);

export const selectIsClaimingAllRewards = withStatus.selector(
  (state) => state.status.claimAllRewardsDataState === 'pending',
);

export const selectPendingRewardsCount = withStatus.selector(
  (state) => state.status.data?.pendingRewards.length,
);

declare global {
  interface LazyLoadedSlices {
    status: ReturnType<typeof persistedReducer>;
  }
}
